import { useEffect, useRef, useState } from 'react';
import maplibregl, { Marker, Popup } from 'maplibre-gl';
import { useMapStore } from '@/stores/mapStore';
import { useTrafficStore } from './trafficStore';
import { trafficService, type TrafficFlow, type TrafficIncident, type Bbox } from '@/services/trafficService';

// Live-traffic overlay. Renders congestion flow as graduated colored circles (the
// served flow geometry is a per-segment GeoJSON Point) and incidents as warning
// markers. Fetches for the current viewport, debounced on map move, and cleans up
// its source/layers/markers whenever toggled off or unmounted.

const FLOW_SOURCE_ID = 'traffic-flow';
const FLOW_CIRCLE_LAYER_ID = 'traffic-flow-circles';

// Below this zoom a city-wide viewport would pull far too many segments to render
// legibly, so we skip fetching and prompt the user to zoom in.
const MIN_ZOOM = 11;
// Hard cap on rendered segments (most-congested first) to keep the map readable.
const MAX_SEGMENTS = 400;
// Debounce window for refetching after the map settles.
const DEBOUNCE_MS = 1500;

type Status = 'idle' | 'loading' | 'ok' | 'empty' | 'zoomed-out' | 'error';

// condition -> color. free/light = green, moderate = amber, heavy = orange,
// severe = red; anything unexpected falls back to a neutral gray.
const CONDITION_COLOR_EXPR: maplibregl.ExpressionSpecification = [
  'match',
  ['get', 'condition'],
  'free', '#34a853',
  'light', '#34a853',
  'moderate', '#fbbc04',
  'heavy', '#f9640a',
  'severe', '#ea4335',
  '#9aa0a6',
];

function severityColor(severity: string): string {
  switch (severity) {
    case 'severe':
    case 'heavy':
      return '#ea4335';
    case 'moderate':
      return '#f9640a';
    default:
      return '#fbbc04';
  }
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function toTitle(value: string): string {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function buildFlowFeatureCollection(flows: TrafficFlow[]): GeoJSON.FeatureCollection {
  const withGeometry = flows.filter(
    (f) => f.geometry && Array.isArray(f.geometry.coordinates) && f.geometry.coordinates.length >= 2
  );

  // Cap to the most-congested segments so a dense viewport stays readable.
  const capped =
    withGeometry.length > MAX_SEGMENTS
      ? [...withGeometry].sort((a, b) => b.congestion_level - a.congestion_level).slice(0, MAX_SEGMENTS)
      : withGeometry;

  return {
    type: 'FeatureCollection',
    features: capped.map((f) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [f.geometry!.coordinates[0], f.geometry!.coordinates[1]],
      },
      properties: {
        segment_id: f.segment_id,
        condition: f.condition,
        congestion_level: f.congestion_level,
        current_speed: f.current_speed,
        free_flow_speed: f.free_flow_speed,
      },
    })),
  };
}

function createIncidentElement(incident: TrafficIncident): HTMLElement {
  const el = document.createElement('div');
  el.className = 'traffic-incident-marker';
  const color = severityColor(incident.severity);
  // Warning triangle with an exclamation mark, distinct from search/waypoint pins.
  el.innerHTML = `
    <div class="w-7 h-7 drop-shadow-md">
      <svg viewBox="0 0 24 24" width="28" height="28">
        <path d="M12 2L23 21H1L12 2Z" fill="${color}" stroke="white" stroke-width="1.5" stroke-linejoin="round"/>
        <rect x="11" y="9" width="2" height="6" rx="1" fill="white"/>
        <rect x="11" y="16.5" width="2" height="2" rx="1" fill="white"/>
      </svg>
    </div>
  `;
  return el;
}

function createIncidentPopup(incident: TrafficIncident): Popup {
  const type = escapeHtml(toTitle(incident.type) || 'Incident');
  const severity = escapeHtml(toTitle(incident.severity));
  const description = escapeHtml(incident.description || '');
  const color = severityColor(incident.severity);
  return new Popup({ offset: 20, closeButton: true }).setHTML(`
    <div class="p-2 max-w-[220px]">
      <div class="flex items-center gap-2">
        <span class="inline-block w-2.5 h-2.5 rounded-full" style="background:${color}"></span>
        <span class="font-medium text-gray-900">${type}</span>
        ${severity ? `<span class="text-xs text-gray-500">${severity}</span>` : ''}
      </div>
      ${description ? `<div class="mt-1 text-sm text-gray-600">${description}</div>` : ''}
    </div>
  `);
}

export function TrafficLayer() {
  const { map, isLoaded } = useMapStore();
  const enabled = useTrafficStore((s) => s.enabled);
  const [status, setStatus] = useState<Status>('idle');

  // Incident markers keyed by incident id, and a monotonically increasing request
  // id so stale responses (e.g. a request whose viewport was superseded and whose
  // HTTP call was aborted) never overwrite fresher data or flash an error.
  const markersRef = useRef<Map<string, Marker>>(new Map());
  const reqIdRef = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!map || !isLoaded) return;

    // When disabled, ensure everything is torn down and stop here.
    if (!enabled) {
      setStatus('idle');
      return;
    }

    let active = true;

    const clearMarkers = () => {
      markersRef.current.forEach((m) => m.remove());
      markersRef.current.clear();
    };

    const addSourceAndLayers = () => {
      if (!map.getSource(FLOW_SOURCE_ID)) {
        map.addSource(FLOW_SOURCE_ID, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        });
      }
      if (!map.getLayer(FLOW_CIRCLE_LAYER_ID)) {
        map.addLayer({
          id: FLOW_CIRCLE_LAYER_ID,
          type: 'circle',
          source: FLOW_SOURCE_ID,
          paint: {
            'circle-color': CONDITION_COLOR_EXPR,
            'circle-radius': [
              'interpolate',
              ['linear'],
              ['zoom'],
              11, 3,
              14, 5,
              17, 7,
            ],
            'circle-opacity': 0.85,
            'circle-stroke-width': 1,
            'circle-stroke-color': '#ffffff',
            'circle-stroke-opacity': 0.6,
          },
        });
      }
    };

    const syncIncidents = (incidents: TrafficIncident[]) => {
      const current = markersRef.current;
      const nextIds = new Set(
        incidents
          .filter((i) => i.location && Array.isArray(i.location.coordinates) && i.location.coordinates.length >= 2)
          .map((i) => i.id)
      );

      // Remove markers no longer present.
      current.forEach((marker, id) => {
        if (!nextIds.has(id)) {
          marker.remove();
          current.delete(id);
        }
      });

      // Add markers for new incidents (existing ones are left in place).
      incidents.forEach((incident) => {
        if (!incident.location || !Array.isArray(incident.location.coordinates)) return;
        if (current.has(incident.id)) return;
        const [lng, lat] = incident.location.coordinates;
        const marker = new Marker({ element: createIncidentElement(incident), anchor: 'bottom' })
          .setLngLat([lng, lat])
          .setPopup(createIncidentPopup(incident))
          .addTo(map);
        current.set(incident.id, marker);
      });
    };

    const refresh = async () => {
      if (!active) return;
      const source = map.getSource(FLOW_SOURCE_ID) as maplibregl.GeoJSONSource | undefined;

      // Too far out: clearing avoids dumping thousands of segments on the map.
      if (map.getZoom() < MIN_ZOOM) {
        source?.setData({ type: 'FeatureCollection', features: [] });
        clearMarkers();
        setStatus('zoomed-out');
        return;
      }

      const b = map.getBounds();
      const bbox: Bbox = {
        west: b.getWest(),
        south: b.getSouth(),
        east: b.getEast(),
        north: b.getNorth(),
      };

      const myId = ++reqIdRef.current;
      setStatus('loading');
      try {
        const [flows, incidents] = await Promise.all([
          trafficService.getFlow(bbox),
          trafficService.getIncidents(bbox),
        ]);
        // Ignore stale/superseded responses.
        if (!active || myId !== reqIdRef.current) return;

        const liveSource = map.getSource(FLOW_SOURCE_ID) as maplibregl.GeoJSONSource | undefined;
        liveSource?.setData(buildFlowFeatureCollection(flows));
        syncIncidents(incidents);

        setStatus(flows.length === 0 && incidents.length === 0 ? 'empty' : 'ok');
      } catch {
        // Only surface an error for the latest request; aborted stale requests
        // (superseded viewport) are silently ignored.
        if (!active || myId !== reqIdRef.current) return;
        setStatus('error');
      }
    };

    const scheduleRefresh = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(refresh, DEBOUNCE_MS);
    };

    // A base-style switch (map.setStyle) drops custom sources/layers; re-add and
    // refetch so the overlay survives a Default/Dark/Satellite change.
    const onStyleLoad = () => {
      if (!active) return;
      addSourceAndLayers();
      refresh();
    };

    addSourceAndLayers();
    refresh();
    map.on('moveend', scheduleRefresh);
    map.on('zoomend', scheduleRefresh);
    map.on('style.load', onStyleLoad);

    return () => {
      active = false;
      // Invalidate any in-flight request so its late resolve is ignored.
      reqIdRef.current++;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      map.off('moveend', scheduleRefresh);
      map.off('zoomend', scheduleRefresh);
      map.off('style.load', onStyleLoad);
      clearMarkers();
      if (map.getLayer(FLOW_CIRCLE_LAYER_ID)) map.removeLayer(FLOW_CIRCLE_LAYER_ID);
      if (map.getSource(FLOW_SOURCE_ID)) map.removeSource(FLOW_SOURCE_ID);
    };
  }, [map, isLoaded, enabled]);

  if (!enabled) return null;

  return (
    <>
      {/* Legend (only once live data is shown) */}
      {status === 'ok' && (
        <div className="absolute bottom-24 left-4 z-controls rounded-lg bg-white/95 px-3 py-2 text-xs shadow-card backdrop-blur">
          <div className="mb-1 font-medium text-gray-700">Live traffic</div>
          <div className="flex flex-col gap-1">
            <LegendRow color="#34a853" label="Free / light" />
            <LegendRow color="#fbbc04" label="Moderate" />
            <LegendRow color="#f9640a" label="Heavy" />
            <LegendRow color="#ea4335" label="Severe" />
          </div>
        </div>
      )}

      {/* Subtle status hints for the non-happy paths. No fabricated data is ever shown. */}
      {(status === 'empty' || status === 'zoomed-out' || status === 'error') && (
        <div className="absolute bottom-24 left-1/2 z-controls -translate-x-1/2 rounded-full bg-white/95 px-3 py-1 text-xs text-gray-600 shadow-card backdrop-blur">
          {status === 'zoomed-out'
            ? 'Zoom in to see live traffic'
            : status === 'error'
              ? 'Live traffic unavailable'
              : 'No live traffic here'}
        </div>
      )}
    </>
  );
}

function LegendRow({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-2 text-gray-600">
      <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: color }} />
      <span>{label}</span>
    </div>
  );
}
